import { motion } from "framer-motion";
import { Check, Lock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FloorPlan3D } from "./FloorPlan3D";

const glass = "rounded-2xl border border-white/[0.09] bg-white/[0.04] backdrop-blur-sm";
const kick = "text-[10px] font-mono-lux tracking-[0.28em] uppercase text-white/45";

const PickTime = () => {
  const { t } = useLang();
  const times = ["18:00", "18:45", "19:15", "19:30", "20:00", "20:45", "21:15", "21:30"];
  return (
    <div className={`${glass} p-6 sm:p-7 space-y-5`}>
      <div className="flex gap-6">
        <div><p className={kick}>{t("Guests")}</p><p className="font-serif-lux text-3xl text-[#F5EFE2] mt-1" data-no-translate>4</p></div>
        <div><p className={kick}>{t("Service")}</p><p className="font-serif-lux text-3xl text-[#F5EFE2] mt-1">{t("Dinner")}</p></div>
      </div>
      <div>
        <p className={`${kick} mb-2.5`}>{t("Available times")}</p>
        <div className="grid grid-cols-4 gap-2">
          {times.map((tm, i) => (
            <motion.span key={tm} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.07 }}
              className={`text-center py-2 rounded-full font-mono-lux text-[13px] border ${tm === "19:30" ? "bg-[#D4AF37] border-[#D4AF37] text-[#1A1408] shadow-[0_0_24px_rgba(212,175,55,0.5)]" : "border-white/12 text-white/75"}`} data-no-translate>{tm}</motion.span>
          ))}
        </div>
      </div>
    </div>
  );
};

const FindTable = () => (
  <div className={`${glass} p-6 sm:p-7`}>
    <FloorPlan3D busyIds={["T1", "T4", "T6", "T8"]} chosenIds={["T3"]} />
  </div>
);

const Hold = () => {
  const { t } = useLang();
  return (
    <div className={`${glass} p-8 flex flex-col items-center`}>
      <div className="relative w-44 h-44">
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
          <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
          <motion.circle cx="50" cy="50" r="45" fill="none" stroke="#D4AF37" strokeWidth="2.4" strokeLinecap="round" pathLength="1"
            initial={{ pathLength: 1 }} animate={{ pathLength: 0.82 }} transition={{ duration: 5.5, ease: "linear" }}
            style={{ filter: "drop-shadow(0 0 6px rgba(212,175,55,0.7))" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-serif-lux text-4xl text-[#F5EFE2]" data-no-translate>10:00</span>
          <span className={`${kick} mt-1`}>{t("Table held")}</span>
        </div>
      </div>
      <p className="mt-5 text-sm text-white/60"><span data-no-translate>T3 · 19:30 · </span>{t("4 guests")}</p>
    </div>
  );
};

const Deposit = () => {
  const { t } = useLang();
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-[#D4AF37]/35 p-6" style={{ background: "linear-gradient(160deg, rgba(212,175,55,0.14), rgba(20,16,10,0.55))" }}>
        <p className="text-[13px] text-[#F5EFE2]">{t("Deposit needed")}</p>
        <p className="font-serif-lux text-5xl text-[#F6E3A6] mt-2" data-no-translate>£40.00</p>
        <p className="text-[12.5px] text-white/55 mt-2" data-no-translate>£10.00 × 4</p>
      </div>
      <div className={`${glass} px-5 py-4 flex items-center gap-3`}>
        <span className="font-serif-lux text-[15px] text-[#F5EFE2]">ARI</span>
        <motion.span className="flex-1 h-px origin-left" style={{ background: "linear-gradient(90deg,#D4AF37,rgba(212,175,55,0.2))" }}
          initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }} />
        <motion.span className="rounded-full border border-[#D4AF37]/50 px-3 py-1.5 text-[12.5px] text-[#F6E3A6] inline-flex items-center gap-1.5"
          initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3 }}>
          <Lock className="w-3.5 h-3.5" />{t("Your payment provider")}
        </motion.span>
      </div>
      <motion.div className={`${glass} px-5 py-3 text-[12.5px] text-white/70 flex items-center justify-between`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}>
        <span data-no-translate>ARI-7K2M9Q</span><span className="text-[#FBECA1] uppercase tracking-[0.16em] text-[10.5px]">{t("Awaiting payment")}</span>
      </motion.div>
    </div>
  );
};

const Confirmed = () => {
  const { t } = useLang();
  const rows = [["19:00", "Harper", "2"], ["19:30", "Amelia Hart", "4"], ["20:15", "Okafor", "6"]];
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-[#D4AF37]/40 p-6" style={{ background: "linear-gradient(160deg, rgba(212,175,55,0.12), rgba(20,16,10,0.6))" }}>
        <p className="flex items-center gap-2 text-[#E9CE7A] text-[11px] font-mono-lux tracking-[0.28em] uppercase"><Check className="w-3.5 h-3.5" />{t("Confirmed")}</p>
        <p className="font-serif-lux text-3xl text-[#F5EFE2] mt-2" data-no-translate>ARI-7K2M9Q</p>
      </div>
      <div className={`${glass} p-4`}>
        <p className={`${kick} mb-3`}>{t("Reservations board · live")}</p>
        {rows.map(([tm, n, p], i) => (
          <motion.div key={tm} className={`flex items-center justify-between rounded-lg px-3 py-2 text-[13px] ${i === 1 ? "bg-[#D4AF37]/12 text-[#F6E3A6]" : "text-white/65"}`}
            initial={i === 1 ? { opacity: 0, x: 24 } : false} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6, duration: 0.7 }} data-no-translate>
            <span className="font-mono-lux">{tm}</span><span>{n}</span><span>{p}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export const SLIDES = [
  { id: "time", title: "The guest picks a time", Visual: PickTime,
    caption: "Your guest chooses a day, the party size and lunch or dinner. ARI only shows times when a suitable table is free." },
  { id: "table", title: "ARI finds the right table", Visual: FindTable,
    caption: "The party is seated at the smallest suitable table, keeping larger tables for larger groups and spreading bookings evenly. Bigger parties can have up to three tables joined." },
  { id: "hold", title: "The table is held", Visual: Hold,
    caption: "While the guest adds their details, ARI holds the table for 10 minutes so nobody else can take it." },
  { id: "deposit", title: "Payments stay with your provider", Visual: Deposit,
    caption: "ARI never takes the payment. When a deposit applies, ARI hands the guest to your own payment provider and holds the booking as awaiting payment until your team marks it paid." },
  { id: "confirmed", title: "Confirmed, and your team sees it live", Visual: Confirmed,
    caption: "The guest gets a reference and a confirmation email, their guest profile is updated, and the booking appears instantly on your Reservations board." },
];
